import React, { useEffect, useState } from "react";
import { TextField } from "components/ui/TextField";
import { Button } from "components/ui/Button";
import { Card } from "components/ui/Card";
import {
  getOrgSettings,
  upsertOrgSettings,
  listNumbering,
  updateNumbering,
} from "api/settings";
import { useAuth } from "auth/AuthProvider";

export default function SettingsPage() {
  const { profile } = useAuth();
  const isAdmin = profile?.role === "superadmin" || profile?.role === "admin";

  const [org, setOrg] = useState(null);
  const [num, setNum] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingOrg, setSavingOrg] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [o, n] = await Promise.all([getOrgSettings(), listNumbering()]);
      setOrg(o);
      // Ensure n is an array and filter out null/undefined values
      const numArray = Array.isArray(n) ? n.filter(item => item != null) : (n ? Object.values(n).filter(item => item != null) : []);
      setNum(numArray);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveOrg = async () => {
    if (!isAdmin) return;
    setSavingOrg(true);
    setErr(null);
    try {
      const saved = await upsertOrgSettings(org);
      setOrg(saved);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSavingOrg(false);
    }
  };

  const saveRow = async (row) => {
    if (!isAdmin) return;
    setSavingId(row.id);
    try {
      const saved = await updateNumbering(row.id, {
        prefix: row.prefix,
        mask: row.mask,
      });
      setNum((prev) => prev.map((x) => (x.id === row.id ? saved : x)));
    } catch (e) {
      alert(e.message);
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <div className="p-6 text-slate-900 dark:text-slate-100">Загрузка…</div>;
  if (err) return <div className="p-6 text-red-600 dark:text-red-400">Ошибка: {err}</div>;

  return (
    <div className="space-y-6">
      <Card title="Организация">
        <div className="grid md:grid-cols-2 gap-3">
          <TextField
            label="Название"
            value={org?.org_name || ""}
            onChange={(e) => setOrg({ ...org, org_name: e.target.value })}
            disabled={!isAdmin}
          />
          <TextField
            label="ИНН"
            value={org?.inn || ""}
            onChange={(e) => setOrg({ ...org, inn: e.target.value })}
            disabled={!isAdmin}
          />
          <TextField
            label="Адрес"
            value={org?.address || ""}
            onChange={(e) => setOrg({ ...org, address: e.target.value })}
            disabled={!isAdmin}
          />
          <TextField
            label="Телефон"
            value={org?.phone || ""}
            onChange={(e) => setOrg({ ...org, phone: e.target.value })}
            disabled={!isAdmin}
          />
          <TextField
            label="Директор"
            value={org?.director_name || ""}
            onChange={(e) => setOrg({ ...org, director_name: e.target.value })}
            disabled={!isAdmin}
          />
          <TextField
            label="Logo URL"
            value={org?.logo_url || ""}
            onChange={(e) => setOrg({ ...org, logo_url: e.target.value })}
            disabled={!isAdmin}
          />
        </div>
        <div className="mt-3">
          <Button onClick={saveOrg} disabled={!isAdmin || savingOrg} loading={savingOrg}>
            Сохранить
          </Button>
          {!isAdmin && (
            <span className="text-xs opacity-70 ml-3 text-slate-600 dark:text-slate-400">
              Изменения доступны только superadmin
            </span>
          )}
        </div>
      </Card>

      <Card title="Нумерация актов">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left opacity-70 text-slate-700 dark:text-slate-300">
                <th className="py-2">Тип</th>
                <th>Префикс</th>
                <th>Маска</th>
                <th>Последовательность</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {num.map((row) => (
                <tr key={row.id} className="border-t border-slate-200 dark:border-slate-700">
                  <td className="py-2 text-slate-900 dark:text-slate-100">{row.id}</td>
                  <td>
                    <input
                      className="rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 transition-colors"
                      value={row.prefix}
                      onChange={(e) =>
                        setNum((prev) =>
                          prev.map((x) =>
                            x.id === row.id
                              ? { ...x, prefix: e.target.value }
                              : x
                          )
                        )
                      }
                      disabled={!isAdmin || savingId === row.id}
                    />
                  </td>
                  <td>
                    <input
                      className="rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 w-[280px] transition-colors"
                      value={row.mask || ""}
                      onChange={(e) =>
                        setNum((prev) =>
                          prev.map((x) =>
                            x.id === row.id ? { ...x, mask: e.target.value } : x
                          )
                        )
                      }
                      disabled={!isAdmin || savingId === row.id}
                    />
                  </td>
                  <td className="text-slate-900 dark:text-slate-100">{row.last_seq}</td>
                  <td>
                    <Button
                      variant="outline"
                      onClick={() => saveRow(row)}
                      disabled={!isAdmin || savingId === row.id}
                      loading={savingId === row.id}
                    >
                      Сохранить
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
